import React from 'react';
import { 
  FileText, User, Phone, Mail, Calendar as CalendarIcon, 
  MapPin, MessageSquare, Shield, Building, Camera, 
  Clock, DollarSign, CheckCircle
} from 'lucide-react';

/**
 * CustomFieldsSection Component
 * Dynamically renders custom fields created in ReservationSettings
 * Features:
 * - Supports all field types (text, email, phone, number, date, datetime, textarea, select, checkbox, url)
 * - Handles validation patterns
 * - Shows/hides based on visibility settings
 * - Required field indicators
 * - Icons and descriptions
 * - Change tracking in edit mode
 * Extracted from SimplifiedReservationForm for better code organization
 */
const CustomFieldsSection = ({
  reservationSettings,
  reservationData,
  handleEnhancedInputChange,
  fieldValidation,
  isSaving,
  uploadingDocuments,
  editIndex,
  fieldChanges = {}
}) => {
  
  // Get icon component for field
  const getFieldIcon = (iconName) => {
    const icons = {
      User, Phone, Mail, Shield, Calendar: CalendarIcon, MapPin, 
      CheckCircle, MessageSquare, FileText, Building, Camera, 
      Clock, DollarSign
    };
    return icons[iconName] || FileText;
  };

  // Get custom fields (fields marked as isCustom)
  const getCustomFields = () => {
    if (!reservationSettings?.fieldSettings) return [];
    
    return Object.entries(reservationSettings.fieldSettings)
      .filter(([fieldKey, fieldConfig]) => fieldConfig.isCustom && fieldConfig.visible)
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Render field based on type
  const renderCustomField = (fieldKey, fieldConfig) => {
    const IconComponent = getFieldIcon(fieldConfig.icon);
    const value = reservationData[fieldKey] || '';
    const hasError = fieldValidation[fieldKey]?.length > 0;
    const hasChanges = fieldChanges[fieldKey];

    // Common input classes
    const inputClasses = `w-full p-3 bg-gray-700 border ${
      hasError ? 'border-red-500' : 'border-gray-600'
    } text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
      isSaving || uploadingDocuments ? 'opacity-50 cursor-not-allowed' : ''
    }`;

    // Field label
    const fieldLabel = (
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-300 flex items-center gap-2">
          {reservationSettings.formBehavior?.showFieldIcons && (
            <IconComponent size={16} className="text-orange-400" />
          )}
          {fieldConfig.label}
          {fieldConfig.required && <span className="text-red-500">*</span>}
          {editIndex !== null && hasChanges && (
            <span className="ml-2 px-2 py-0.5 bg-orange-900/30 text-orange-400 text-xs rounded-full border border-orange-700">
              Changed
            </span>
          )}
        </label>
      </div>
    );

    // Field description
    const fieldDescription = reservationSettings.formBehavior?.showFieldDescriptions && fieldConfig.description && (
      <p className="text-xs text-gray-400 mb-2">{fieldConfig.description}</p>
    );

    // Render based on field type
    switch (fieldConfig.fieldType) {
      case 'textarea':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <textarea
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              maxLength={fieldConfig.maxLength}
              className={inputClasses + ' min-h-24 resize-none'}
              disabled={isSaving || uploadingDocuments}
            />
            {reservationSettings.formBehavior?.showCharacterCounts && fieldConfig.maxLength && (
              <div className="text-xs text-gray-400 text-right mt-1">
                {value.length}/{fieldConfig.maxLength}
              </div>
            )}
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <select
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            >
              <option value="">Select {fieldConfig.label}</option>
              {fieldConfig.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={fieldKey}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value === true || value === 'true'}
                onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.checked, fieldConfig)}
                className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
                disabled={isSaving || uploadingDocuments}
              />
              <span className="text-gray-300 text-sm">
                {fieldConfig.label}
                {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              {editIndex !== null && hasChanges && (
                <span className="ml-2 px-2 py-0.5 bg-orange-900/30 text-orange-400 text-xs rounded-full border border-orange-700">
                  Changed
                </span>
              )}
            </label>
            {fieldDescription && (
              <p className="text-xs text-gray-400 ml-6 mt-1">{fieldConfig.description}</p>
            )}
            {hasError && (
              <p className="text-red-400 text-sm ml-6 mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="date"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'datetime':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="number"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'email':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="email"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'phone':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="tel"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'url':
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="url"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div key={fieldKey}>
            {fieldLabel}
            {fieldDescription}
            <input
              type="text"
              value={value}
              onChange={(e) => handleEnhancedInputChange(fieldKey, e.target.value, fieldConfig)}
              placeholder={fieldConfig.placeholder}
              maxLength={fieldConfig.maxLength}
              pattern={fieldConfig.validationPattern || undefined}
              className={inputClasses}
              disabled={isSaving || uploadingDocuments}
            />
            {reservationSettings.formBehavior?.showCharacterCounts && fieldConfig.maxLength && (
              <div className="text-xs text-gray-400 text-right mt-1">
                {value.length}/{fieldConfig.maxLength}
              </div>
            )}
            {hasError && (
              <p className="text-red-400 text-sm mt-1">{fieldValidation[fieldKey][0]}</p>
            )}
          </div>
        );
    }
  };

  const customFields = getCustomFields();
  
  // Don't render section if no custom fields
  if (customFields.length === 0) return null;

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
          <FileText size={16} className="text-white" />
        </div>
        <div>
          <h3 className="text-white font-medium">Additional Information</h3>
          {/* ✅ CHANGED: "1 custom field" count line removed */}
        </div>
      </div>

      {/* Custom Fields Grid */}
      <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
        <div className={
          reservationSettings.formBehavior?.compactMode 
            ? 'grid grid-cols-1 md:grid-cols-2 gap-4' 
            : 'space-y-4'
        }>
          {customFields.map(([fieldKey, fieldConfig]) => 
            renderCustomField(fieldKey, fieldConfig)
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomFieldsSection;